/**
 * Visualization Registry
 *
 * Maps node types to visualization components.
 * Plugins register their visualizations via registerUI, called at app init.
 *
 * Visualizations receive:
 * - Base props (width, height, video methods, audio methods)
 * - Data props from optional getProps selector
 *
 * The getProps selector pattern separates data extraction from rendering,
 * making visualizations more reusable across different node types.
 */

import React from 'react';
import type { VizBaseProps, VizDataContext, VizPropsSelector, VisualizationProps } from './VisualisationProps';
import type { GraphDefinition } from '@/graph/GraphDefinition';
import type { Visualiser } from '@/ui/performance/VisualiserRegistry';
import { registerVisualiser, registerBaseMapping } from '@/ui/performance/VisualiserRegistry';

export type { VisualizationProps, VizBaseProps, VizDataContext, VizPropsSelector };

/**
 * Visualization component type.
 * Receives base props + data props from getProps selector (if provided).
 */
type VisualizationComponent<TData = Record<string, any>> = React.ComponentType<VizBaseProps & TData>;

/**
 * Registration entry storing component and optional getProps selector
 */
interface VizRegistration {
  component: VisualizationComponent<any>;
  getProps?: VizPropsSelector<any>;
}

// Registry: qualified node type -> registration
const visualizationRegistry: Record<string, VizRegistration> = {};

// Version counter - increments when visualizations are registered
let registryVersion = 0;
const versionListeners: Set<() => void> = new Set();

/**
 * Get current registry version (for React dependency tracking)
 */
export function getRegistryVersion(): number {
  return registryVersion;
}

/**
 * Subscribe to registry changes
 */
export function subscribeToRegistry(callback: () => void): () => void {
  versionListeners.add(callback);
  return () => versionListeners.delete(callback);
}

/**
 * Get visualization registration by node type
 */
export function getVisualization(nodeType: string): VizRegistration | null {
  return visualizationRegistry[nodeType] ?? null;
}

/**
 * Register a visualization component for a node type
 * @param nodeType - The qualified node type
 * @param component - The visualization component
 * @param getProps - Optional selector to extract data props from context
 */
export function registerVisualization(
  nodeType: string,
  component: VisualizationComponent<any>,
  getProps?: VizPropsSelector<any>
): void {
  visualizationRegistry[nodeType] = { component, getProps };
  registryVersion++;
  versionListeners.forEach(cb => cb());
}

/**
 * Context passed to plugin.registerUI for visualization registration
 */
export interface RegisterUIContext {
  /**
   * Register a visualization for a node type.
   * @param nodeType - The node type (will be auto-namespaced)
   * @param component - The visualization component
   * @param getProps - Optional selector to extract data props from context
   */
  registerViz: <TData = Record<string, any>>(
    nodeType: string,
    component: VisualizationComponent<TData>,
    getProps?: VizPropsSelector<TData>
  ) => void;

  /**
   * Register a performance mode visualiser.
   * @param name - Unique visualiser name (e.g., 'circle')
   * @param visualiser - The visualiser definition
   */
  registerVisualiser: (name: string, visualiser: Visualiser) => void;

  /**
   * Declare the default visualiser for a node type.
   * @param nodeType - The node type (will be auto-namespaced)
   * @param visualiserName - The visualiser name to map to
   * @param params - Maps visualiser property names to endpoint ids (e.g. { radius: 'frequency' })
   */
  registerBaseMapping: (nodeType: string, visualiserName: string, params?: Record<string, string>) => void;
}

/**
 * Create a RegisterUIContext bound to a plugin's namespace
 */
export function createRegisterUIContext(namespace?: string): RegisterUIContext {
  const qualify = (type: string) => namespace ? `${namespace}.${type}` : type;

  return {
    registerViz: <TData = Record<string, any>>(
      nodeType: string,
      component: VisualizationComponent<TData>,
      getProps?: VizPropsSelector<TData>
    ) => {
      registerVisualization(qualify(nodeType), component, getProps);
    },

    registerVisualiser: (name: string, visualiser: Visualiser) => {
      registerVisualiser(name, visualiser);
    },

    registerBaseMapping: (nodeType: string, visualiserName: string, params?: Record<string, string>) => {
      registerBaseMapping(qualify(nodeType), visualiserName, params);
    },
  };
}

/**
 * Resolve the visualization type for a node.
 * - For primitives with registered visualizations: returns the node type
 * - For graphs: recursively follows vizNode until reaching a primitive with a visualization
 *
 * @param nodeType - The node type to resolve
 * @param getDefinition - Function to lookup a GraphDefinition by type/id
 * @returns The resolved visualization type, or undefined if no visualization
 */
export function resolveVizType(
  nodeType: string,
  getDefinition: (id: string) => GraphDefinition | undefined
): string | undefined {
  // Check if this type has a registered visualization
  if (nodeType in visualizationRegistry) {
    return nodeType;
  }

  // Not a registered primitive - check if it's a graph definition with vizNode
  const graphDef = getDefinition(nodeType);
  if (!graphDef?.vizNode) {
    return undefined; // No visualization for this graph
  }

  // Get the type of the vizNode
  const vizNodeConfig = graphDef.nodes[graphDef.vizNode];
  if (!vizNodeConfig) {
    return undefined;
  }

  // Handle both string format ("three.Canvas") and object format ({ type: "three.Canvas" })
  const vizNodeType = typeof vizNodeConfig === 'string' ? vizNodeConfig : vizNodeConfig.type;

  // Recursively resolve (vizNode could itself be a graph)
  return resolveVizType(vizNodeType, getDefinition);
}
